import type {ReactNode} from 'react';
import {Box, Flex, Grid, Text} from '@chakra-ui/react';
import MetaPill from '../common/MetaPill';
import {isNonEnglish, languageFlag} from '../../constants/designTokens';
import {patientDisplayNameFromRecord} from '../../utils/patientNames';
import type {PlannerPatient} from './plannerModels';
import {plannerPatientMetadataParts} from './plannerModels';

export interface PlannerCardLine {
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  noOfLines?: number;
  node?: ReactNode;
  // Renders a step lighter/smaller — used for the recent-row
  // "<duration> • Interpreter/Scribe" caption.
  subtle?: boolean;
  text: string;
  whiteSpace?: string;
}

export type PatientIdentityRow =
  | {
      kind: 'patient';
      patient: PlannerPatient;
      date?: string;
      metadataNode?: ReactNode;
      nameNode?: ReactNode;
      testId?: string;
      time?: string;
    }
  | {
      kind: 'label';
      label: string;
      date?: string;
      flag?: string;
      language?: string;
      metadataLines?: Array<string | PlannerCardLine>;
      nameNode?: ReactNode;
      testId?: string;
      time?: string;
    };

interface ResolvedPatientIdentityRow {
  date?: string;
  flag?: string;
  language?: string;
  metadataLines?: Array<string | PlannerCardLine>;
  name?: string;
  nameNode?: ReactNode;
  testId?: string;
  time?: string;
}

export interface PatientBrowserIdentity {
  ariaContextText: string;
  language: string;
  metadataParts: string[];
  metadataText: string;
  name: string;
}

export interface PlannerTypePill {
  label: string;
  tone?: 'interpreter' | 'scribe' | 'phone' | 'neutral';
  testId?: string;
}

export interface PatientEncounterRow {
  badges?: Array<string | PlannerCardLine>;
  date?: string;
  key?: string;
  // Colored "type of session" chips (Interpreter / Scribe / Phone).
  pills?: PlannerTypePill[];
  // Short trailing meta shown next to the pills, e.g. the duration "18 min".
  meta?: string;
  summary?: string;
  summaryColor?: string;
  summaryFontSize?: string;
  summaryFontWeight?: string;
  summaryNoOfLines?: number;
  summaryNode?: ReactNode;
  summaryProminent?: boolean;
  summaryWhiteSpace?: string;
  testId?: string;
  time?: string;
}

export interface PatientBrowserCardProps {
  cardKind?: string;
  children?: ReactNode;
  contextNode?: ReactNode | null;
  contextText?: string;
  detailLines?: Array<string | PlannerCardLine>;
  encounterRows?: PatientEncounterRow[];
  identityRow?: PatientIdentityRow | false | null;
  identityRowTestId?: string | null;
  language?: string;
  name?: string;
  nameFontWeight?: string | null;
  nameNoOfLines?: number | null;
  nameNode?: ReactNode | null;
  nameWhiteSpace?: string | null;
  showIdentityRow?: boolean;
  showTimeColumn?: boolean;
  singleLine?: boolean;
  styles?: Record<string, any>;
  time?: string;
  timeDetail?: string;
  wrapName?: boolean;
}

export const PLANNER_SIDEBAR_NAME_FONT_SIZE = 'md';
// Prominent encounter summaries are the headline content of a Recent row. Kept
// at the body size ('md') — the patient name above sits at the same size but
// semibold, so weight (not an oversized headline) carries the hierarchy.
export const PLANNER_SIDEBAR_SUMMARY_FONT_SIZE = 'md';
export const PLANNER_SIDEBAR_NORMAL_SUMMARY_FONT_SIZE =
  PLANNER_SIDEBAR_NAME_FONT_SIZE;
export const PLANNER_SIDEBAR_DATE_COLUMN_WIDTH = {
  base: '4.75rem',
  md: '5rem',
};
export const PLANNER_SIDEBAR_DATE_CONTENT_OFFSET = {
  base: '6rem',
  md: '6.25rem',
};
const PLANNER_SIDEBAR_TIMED_ROW_LINE_HEIGHT = '1.25';

export function plannerLanguageMarker(language: any) {
  if (!isNonEnglish(language)) return '';
  return languageFlag(language);
}

const cleanText = (value: any) => String(value ?? '').trim();

const patientDisplayName = (patient: PlannerPatient | null | undefined) => {
  return (
    cleanText(patientDisplayNameFromRecord(patient || {})) || 'Unknown patient'
  );
};

export function buildPatientBrowserIdentity(
  patient: PlannerPatient,
): PatientBrowserIdentity {
  const metadataParts = plannerPatientMetadataParts(patient);
  const metadataText = metadataParts.join(' • ');
  return {
    ariaContextText: metadataText,
    language: patient.language || '',
    metadataParts,
    metadataText,
    name: patientDisplayName(patient),
  };
}

const normalizeDetailLine = (detailLine: any) => {
  if (!detailLine) return null;
  if (typeof detailLine === 'string') return {text: detailLine};
  return detailLine;
};

const normalizedLineItems = (lines: any[] = []) =>
  lines
    .map(normalizeDetailLine)
    .filter((line): line is Record<string, any> =>
      Boolean(line && cleanText(line.text)),
    );

const resolveIdentityRow = ({
  identityRow,
  identityRowTestId,
  time,
  timeDetail,
}: {
  identityRow?: PatientIdentityRow | false | null;
  identityRowTestId?: string | null;
  time?: string;
  timeDetail?: string;
}): ResolvedPatientIdentityRow | null => {
  if (!identityRow) return null;
  if (identityRow.kind === 'patient') {
    const identity = buildPatientBrowserIdentity(identityRow.patient);
    return {
      date: identityRow.date ?? timeDetail,
      language: identity.language,
      metadataLines: identity.metadataText
        ? [{text: identity.metadataText, node: identityRow.metadataNode}]
        : [],
      name: identity.name,
      nameNode: identityRow.nameNode,
      testId: identityRow.testId || identityRowTestId || undefined,
      time: identityRow.time ?? time,
    };
  }

  return {
    date: identityRow.date ?? timeDetail,
    flag: identityRow.flag,
    language: identityRow.language || '',
    metadataLines: identityRow.metadataLines || [],
    name: identityRow.label || 'Unknown patient',
    nameNode: identityRow.nameNode,
    testId: identityRow.testId || identityRowTestId || undefined,
    time: identityRow.time ?? time,
  };
};

export const PatientBrowserCard = ({
  cardKind = 'base',
  children = null,
  encounterRows = [],
  time = '',
  name: _legacyName = '',
  language: _legacyLanguage = '',
  contextText: _legacyContextText = '',
  contextNode: _legacyContextNode = null,
  detailLines: _legacyDetailLines = [],
  identityRow = null,
  identityRowTestId = null,
  showIdentityRow = true,
  singleLine = false,
  wrapName = false,
  nameFontWeight = null,
  nameNoOfLines = null,
  nameWhiteSpace = null,
  nameNode: _legacyNameNode = null,
  showTimeColumn = true,
  timeDetail = '',
  styles,
  ...cardProps
}: PatientBrowserCardProps & Record<string, any>) => {
  const resolvedNameFontWeight =
    nameFontWeight || (singleLine ? 'normal' : 'semibold');
  const resolvedNameFontWeightValue =
    resolvedNameFontWeight === 'normal' ? '400' : '600';
  const resolvedIdentityRow =
    identityRow === false || !showIdentityRow
      ? null
      : resolveIdentityRow({
          identityRow,
          identityRowTestId,
          time,
          timeDetail,
        });
  const resolvedEncounterRows = (encounterRows || [])
    .map((encounterRow: any) => {
      if (!encounterRow) return null;
      const summary = normalizeDetailLine(
        encounterRow.summary == null
          ? null
          : {
              text: encounterRow.summary,
              node: encounterRow.summaryNode,
              color: encounterRow.summaryColor,
              fontSize: encounterRow.summaryFontSize,
              fontWeight: encounterRow.summaryFontWeight,
              noOfLines: encounterRow.summaryNoOfLines,
              prominent: encounterRow.summaryProminent !== false,
              whiteSpace: encounterRow.summaryWhiteSpace,
            },
      );
      const badges = normalizedLineItems(
        encounterRow.badges || encounterRow.detailLines || [],
      );
      const pills = Array.isArray(encounterRow.pills) ? encounterRow.pills : [];
      const hasMeta = Boolean(cleanText(encounterRow.meta));
      // A summary object can exist while being visually empty (blank/whitespace
      // text and no node). Treat that as no content so an otherwise-empty row
      // collapses entirely instead of rendering a blank, space-reserving line.
      // Pills (Phone / Interpreter) and trailing meta (duration) are content on
      // their own — a company phone call with no summary still needs its Phone
      // pill — so a row is only dropped when it has nothing at all to show.
      const summaryHasContent = Boolean(
        summary && (cleanText(summary.text) || summary.node),
      );
      if (!summaryHasContent && !badges.length && !pills.length && !hasMeta)
        return null;
      return {
        ...encounterRow,
        badges,
        summary: summaryHasContent ? summary : null,
      };
    })
    .filter(Boolean);

  // When the card reserves a time column (its identity row carries a time),
  // every stacked row keeps that column — empty when it has no time of its own —
  // so encounter summaries stay aligned under the patient name instead of
  // sliding left under the (now absent) time.
  const cardHasTimeColumn = showTimeColumn && resolvedIdentityRow?.time != null;

  const renderTimeColumn = ({
    date = '',
    time: rowTime = '',
  }: Record<string, any>) => {
    if (!rowTime && !date) return null;
    return (
      <Box
        data-planner-row-date-column="true"
        data-planner-row-date-detail={date || undefined}
        minW={0}
        alignSelf="start"
      >
        {rowTime && (
          <Text
            color={styles?.rowTimeColor}
            fontSize={{base: 'sm', md: 'md'}}
            fontWeight="normal"
            lineHeight={PLANNER_SIDEBAR_TIMED_ROW_LINE_HEIGHT}
            noOfLines={1}
            sx={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: '400 !important',
            }}
          >
            {rowTime}
          </Text>
        )}
        {date && (
          <Text
            color={styles?.metaTextColor}
            fontSize="xs"
            fontWeight="normal"
            lineHeight="1.15"
            mt={rowTime ? 0.5 : 0}
            noOfLines={1}
          >
            {date}
          </Text>
        )}
      </Box>
    );
  };

  // Reserve the time-column cell even when a row has no time of its own, so the
  // content column lines up; an empty spacer keeps the grid two-column without
  // claiming to be a date column.
  const renderTimeColumnCell = (row: Record<string, any>, reserve: boolean) => {
    if (!reserve) return null;
    return renderTimeColumn(row) || <Box minW={0} aria-hidden="true" />;
  };

  const renderCardRow = ({
    children: rowChildren,
    hasTimeColumn,
    key,
    minH,
    testId,
  }: Record<string, any>) => (
    <Grid
      key={key}
      data-testid={testId || undefined}
      data-show-time-column={
        testId ? (hasTimeColumn ? 'true' : 'false') : undefined
      }
      data-planner-card-row-min-h={minH || undefined}
      templateColumns={
        hasTimeColumn
          ? {
              base: `${PLANNER_SIDEBAR_DATE_COLUMN_WIDTH.base} minmax(0, 1fr)`,
              md: `${PLANNER_SIDEBAR_DATE_COLUMN_WIDTH.md} minmax(0, 1fr)`,
            }
          : 'minmax(0, 1fr)'
      }
      alignItems={singleLine && !wrapName ? 'center' : 'start'}
      columnGap={2}
      minH={minH}
      minW={0}
      w="100%"
    >
      {rowChildren}
    </Grid>
  );

  const renderIdentityRow = (row: ResolvedPatientIdentityRow) => {
    const rowMarker =
      row.flag != null ? row.flag : plannerLanguageMarker(row.language);
    const rowHasTimeColumn = showTimeColumn && row.time != null;
    const rowName = row.name || 'Unknown patient';

    return renderCardRow({
      hasTimeColumn: rowHasTimeColumn,
      key: 'identity',
      // Let patient rows hug their own content so selectors and compact lists
      // do not reserve a blank band below the metadata line.
      minH: singleLine ? '48px' : 'auto',
      testId: row.testId,
      children: (
        <>
          {rowHasTimeColumn &&
            renderTimeColumn({date: row.date, time: row.time})}
          <Box minW={0}>
            <Flex align="center" gap={2} minW={0}>
              <Box
                flex="1 1 auto"
                minW={0}
                position="relative"
                overflow={wrapName ? 'visible' : 'hidden'}
              >
                <Text
                  as="span"
                  display="block"
                  color={styles?.rowActionColor}
                  fontSize={PLANNER_SIDEBAR_NAME_FONT_SIZE}
                  fontWeight={resolvedNameFontWeight}
                  lineHeight="1.25"
                  noOfLines={nameNoOfLines || undefined}
                  whiteSpace={
                    nameWhiteSpace || (wrapName ? 'normal' : 'nowrap')
                  }
                  overflowWrap={wrapName ? 'break-word' : undefined}
                  sx={{
                    fontWeight: `${resolvedNameFontWeightValue} !important`,
                  }}
                >
                  {row.nameNode || rowName}
                </Text>
              </Box>

              {(rowMarker || !singleLine) && (
                <Text
                  color={styles?.rowActionColor}
                  fontSize="md"
                  lineHeight="1"
                  minW="1.625rem"
                  textAlign="right"
                  aria-hidden="true"
                >
                  {rowMarker}
                </Text>
              )}
            </Flex>

            {normalizedLineItems(row.metadataLines || []).map(
              (detailLine, index) => (
                <Text
                  key={`${detailLine.text}-${index}`}
                  color={detailLine.color || styles?.metaTextColor}
                  fontSize={detailLine.fontSize || 'sm'}
                  fontWeight={detailLine.fontWeight || 'normal'}
                  lineHeight="1.35"
                  mt={1}
                  noOfLines={detailLine.noOfLines || 1}
                >
                  {detailLine.node || detailLine.text}
                </Text>
              ),
            )}
          </Box>
        </>
      ),
    });
  };

  const renderTypePill = (pill: Record<string, any>, pillIndex: number) => {
    const toneKey = pill.tone || 'neutral';
    const tone =
      styles?.pillTones?.[toneKey] || styles?.pillTones?.neutral || {};
    // Render through the shared MetaPill so the radius / padding / shape match
    // every other pill in the app. Neutral pills (e.g. Interpreter) read as
    // quiet metadata; colored pills (Scribe / Phone) carry weight to stand out.
    const isNeutral = toneKey === 'neutral';
    return (
      <MetaPill
        key={`${pill.label}-${pillIndex}`}
        data-testid={pill.testId || 'planner-row-type-pill'}
        data-planner-pill-tone={toneKey}
        bg={tone.bg}
        color={tone.color}
        borderColor={tone.borderColor}
        // Match the squarer corner radius used by the rest of the app's
        // surfaces (cards, rows, boxes) instead of a fully-rounded stadium.
        borderRadius="md"
        fontSize="sm"
        fontWeight={isNeutral ? 'normal' : 'semibold'}
      >
        {pill.label}
      </MetaPill>
    );
  };

  const renderMetaLine = (row: Record<string, any>, hasSummary: boolean) => {
    const pills = Array.isArray(row.pills) ? row.pills : [];
    if (!pills.length && !row.meta) return null;
    return (
      <Flex
        align="center"
        gap={2}
        rowGap={1}
        flexWrap="wrap"
        minW={0}
        mt={hasSummary ? 1.5 : 0}
      >
        {pills.map(renderTypePill)}
        {row.meta && (
          <Text
            as="span"
            data-testid="planner-row-meta"
            color={styles?.metaTextColor}
            fontSize="sm"
            fontWeight="medium"
            lineHeight="1.3"
            noOfLines={1}
            sx={{fontVariantNumeric: 'tabular-nums'}}
          >
            {row.meta}
          </Text>
        )}
      </Flex>
    );
  };

  const renderEncounterRow = (row: Record<string, any>, index: number) => {
    const rowHasOwnTime = Boolean(row.time || row.date);
    const reserveTimeColumn = cardHasTimeColumn || rowHasOwnTime;
    const summaryIsProminent = row.summary?.prominent !== false;
    const summaryFontSize =
      row.summary?.fontSize ||
      (summaryIsProminent
        ? PLANNER_SIDEBAR_SUMMARY_FONT_SIZE
        : PLANNER_SIDEBAR_NORMAL_SUMMARY_FONT_SIZE);
    const metaLine = renderMetaLine(row, Boolean(row.summary));
    return renderCardRow({
      hasTimeColumn: reserveTimeColumn,
      key: row.key || `encounter-${index}`,
      minH: rowHasOwnTime ? '64px' : 'auto',
      testId: row.testId,
      children: (
        <>
          {renderTimeColumnCell(row, reserveTimeColumn)}
          <Box minW={0}>
            {row.summary && (
              <Text
                data-planner-encounter-summary-font-size={summaryFontSize}
                data-planner-encounter-summary-prominent={
                  summaryIsProminent ? 'true' : 'false'
                }
                color={row.summary.color || styles?.rowActionColor}
                fontSize={summaryFontSize}
                fontWeight={row.summary.fontWeight || 'normal'}
                lineHeight={
                  rowHasOwnTime ? PLANNER_SIDEBAR_TIMED_ROW_LINE_HEIGHT : '1.35'
                }
                noOfLines={row.summary.noOfLines || undefined}
                whiteSpace={row.summary.whiteSpace || undefined}
              >
                {row.summary.node || row.summary.text}
              </Text>
            )}
            {metaLine}
            {row.badges.map((badge: any, badgeIndex: number) => (
              <Text
                key={`${badge.text}-${badgeIndex}`}
                color={badge.color || styles?.metaTextColor}
                fontSize={badge.fontSize || 'sm'}
                fontWeight={badge.fontWeight || 'normal'}
                lineHeight="1.35"
                mt={row.summary || metaLine || badgeIndex > 0 ? 1 : 0}
                noOfLines={badge.noOfLines || 1}
              >
                {badge.node || badge.text}
              </Text>
            ))}
          </Box>
        </>
      ),
    });
  };

  return (
    <Grid
      data-patient-browser-card={cardKind}
      // More breathing room between the patient identity and the encounter
      // summary/note below it.
      gap={resolvedIdentityRow && resolvedEncounterRows.length ? 2.5 : 0}
      w="100%"
      minW={0}
      position="relative"
      color={styles?.rowActionColor}
      fontFamily="inherit"
      px={3}
      py={2.5}
      textAlign="left"
      {...cardProps}
      data-show-time-column={showTimeColumn ? 'true' : 'false'}
    >
      {resolvedIdentityRow && renderIdentityRow(resolvedIdentityRow)}
      {resolvedEncounterRows.map(renderEncounterRow)}
      {children && (
        <Box gridColumn="1 / -1" minW={0}>
          {children}
        </Box>
      )}
    </Grid>
  );
};

export default PatientBrowserCard;
